import { MessageType } from '../enum';
import { BaseMessage, BaseNotice, BaseSystemMessage, TypingMessage, SystemListMessage, SystemArticleMessage, UserTransactionMessage, SystemArticleCheckFailureMessage, ResolutionMessage, UserMessageForAI, AiMessageForUser } from '../types';
import { MediaMessage } from '../types/messages/MediaMessage';
import { RequestUserTransactionMessage } from '../types/messages/RequestUserTransactionMessage';

const LIST_MESSAGE_TYPES = [MessageType.HYBRID_LIST, MessageType.CATEGORIES_LIST, MessageType.SECTION_LIST, MessageType.ARTICLE_LIST] as const;

export function isNotice (message: any): message is BaseNotice {
  return message?.type?.startsWith('notice');
}

export function isMessage (message: any): message is BaseMessage {
  return message?.type?.startsWith('message');
}

export function isSystemMessage (message: any): message is BaseSystemMessage {
  return message?.type?.startsWith('message.sys');
}

export function isTypingMessage (message: any): message is TypingMessage {
  return message?.type === MessageType.TYPING;
}

export function isMediaMessage (message: any): message is MediaMessage {
  return message?.type === MessageType.MEDIA;
}

export function isArticleMessage (message: any): message is SystemArticleMessage {
  return (message?.type || '').startsWith('message.sys.article.');
}

export function isSystemArticleSearchMessage (message: any): message is BaseSystemMessage {
  return (message?.type || '').startsWith(MessageType.USER_SEARCH_ARTICLE_MESSAGE);
}

export function isChatMessage (message: any): boolean {
  return (message?.type || '').startsWith('message.chat');
}

export function isAlternativeActionsMessage (message: any): boolean {
  return (message?.type || '').startsWith(MessageType.ALTERNATIVE_ACTIONS);
}

export function isTransactionMessage (message: any): message is UserTransactionMessage {
  return isMessage(message) && message.type === MessageType.USER_TRANSACTIONS;
}

export function isResolutionMessage (message: any): message is BaseSystemMessage {
  return isMessage(message) && isSystemMessage(message) && message.type.startsWith(MessageType.RESOLUTION);
}

export function isPreSupportPromptMessage (message: any): message is BaseSystemMessage {
  return isMessage(message) && isSystemMessage(message) && message.type?.startsWith(MessageType.PRE_SUPPORT_PROMPT);
}
export function isPreSupportPredictionMessage (message: any): message is BaseSystemMessage {
  return isMessage(message) && isSystemMessage(message) && message.type?.startsWith(MessageType.PRE_SUPPORT_PREDICTION);
}

export function isArticleCheckFailureMessage (message: any): message is SystemArticleCheckFailureMessage {
  return isMessage(message) && isSystemMessage(message) && message.type === MessageType.ARTICLE_CHECK_FAILED_MESSAGE;
}

export function isMetadataResolutionMessage (message: any): message is ResolutionMessage {
  return isMessage(message) && isSystemMessage(message) && message.type === MessageType.METADATA_RESOLUTION;
}

export function isAnyOtherHelpResolutionMessage (message: any): message is ResolutionMessage {
  return isMessage(message) && isSystemMessage(message) && message.type === MessageType.ANY_OTHER_HELP_RESOLUTION;
}

export function isFailedResolutionMessage (message: any): message is ResolutionMessage {
  return isMessage(message) && isSystemMessage(message) && message.type === MessageType.FAILED_RESOLUTION_MESSAGE;
}

export function isOverlongAiConversationResolutionMessage (message: any): message is ResolutionMessage {
  return isMessage(message) && isSystemMessage(message) && message.type === MessageType.OVERLONG_AI_CONVERSATION_RESOLUTION;
}

export function isStaleAiConversationResolutionMessage (message: any): message is ResolutionMessage {
  return isMessage(message) && isSystemMessage(message) && message.type === MessageType.STALE_AI_CONVERSATION_RESOLUTION;
}

export function isUserLoginFromAIResolutionMessage (message: any): message is ResolutionMessage {
  return isMessage(message) && isSystemMessage(message) && message.type === MessageType.USER_LOGIN_FROM_AI;
}

/**
 * Check if the message is an interactive message.
 * @param message The message to test.
 */
export function isInteractiveMessage (message: BaseMessage): message is (BaseMessage & { interactiveMessageParts: [string, string] }) {
  return !!message.interactiveMessageParts && message.interactiveMessageParts.length === 2;
}

/**
 * A list message is type of message that have multiple options to view.
 * @param message The message to test.
 */
export function isListMessage (message: any): message is SystemListMessage {
  return message?.type && LIST_MESSAGE_TYPES.includes(message.type);
}

export function isArticlePreviewMessage (message: any): message is SystemArticleMessage {
  return isSystemMessage(message) && message.type === MessageType.ARTICLE_PREVIEW;
}

export function isUserInputRequestTransactionMessage (message: any): message is RequestUserTransactionMessage {
  return isMessage(message) && message.type === MessageType.AGENT_REQUEST_INPUT;
}

export function isUserTransactionSubmissionMessage (message: any): message is RequestUserTransactionMessage {
  return isMessage(message) && message.type === MessageType.USER_SUBMIT_INPUT_TRANSACTION;
}

export function isUserMessageForAI (message: any): message is UserMessageForAI {
  return message?.type === MessageType.USER_MESSAGE_FOR_AI;
}

export function isAiMessageForUser (message: any): message is AiMessageForUser {
  return message?.type === MessageType.AI_CHAT_MESSAGE;
}
